import { DropdownIcon } from "@xola/icons";
import { ButtonGroup, ComboBox, FormGroup, Label, Modal, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField, InputField, RadioInput, requiredString } from "common";
import { useFormikContext } from "formik";
import { isEmpty } from "lodash";
import React from "react";
import { components, ValueContainerProps } from "react-select";
import { Except } from "type-fest";
import {
    CatalogType,
    DisplayStepOption,
    Experience,
    Field,
    FieldPayload,
    FieldType,
    FieldVisibility,
    FieldWithPickupOptions,
    QuestionnairePricingStructure,
} from "types";
import { array, object, string } from "yup";
import { Form } from "../../Common/Form";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLengthCounter } from "../../Field/ContentLengthCounter";
import { QuestionTypeField } from "./Field/QuestionTypeField";
import { UnitMeasurementField } from "./Field/UnitMeasurementField";
import { ValidationRuleField } from "./Field/ValidationRuleField";
import { PickupLocationsList } from "./PickupLocationsList";
import { QuestionFormSettings } from "./QuestionFormSettings";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useQuestionnairePreference } from "@/hooks/questionnaireFormHooks";
import { useInstalledPlugins } from "@/hooks/usePlugins";
import { isBeforePaymentType, isMarketingConsentType } from "@/models/Form/Questionnaire";
import { PredefinedQuestions, QuestionnaireModalType } from "@/models/Form/QuestionnaireModalType";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";
import { hasWaiverPlugin } from "@/stores/globalStore";
import { RosterViewConfiguration } from "@/types/Dashboard";

const validationSchema = object({
    label: requiredString().max(100, "should be less than 100 characters"),
    url: string().url("should be valid URL with http or https").nullable(),
    options: array().test("optionsRequired", "- at least one option is required", (value, form) => {
        const { type, required } = form.parent;
        const isEmpty: boolean = !value || value.length === 0;

        if ((type === FieldType.SELECT || type === FieldType.PICKUP) && required) {
            return !isEmpty;
        }

        return true;
    }),
    description: string().when("url", {
        is: (url: string) => !!url,
        then: string().required("is required when URL is present"),
        otherwise: string().nullable(),
    }),
});

const validateForm =
    (modalType: QuestionnaireModalType, showStepButtons: boolean, displayStep?: DisplayStepOption) =>
    (values: FieldPayload) => {
        const errors: any = {};
        const isAddon = modalType === QuestionnaireModalType.ADDON;
        if (
            displayStep === DisplayStepOption.WAIVER_SIGNING &&
            modalType !== QuestionnaireModalType.BIRTH_DATE &&
            (isEmpty(values.visibility) || !Object.values(FieldVisibility).includes(values.visibility!))
        ) {
            errors.visibility = "- at least one of these should be enabled";
        }

        if (isMarketingConsentType(modalType) && isEmpty(values?.description?.trim())) {
            errors.description = "is required";
        }

        if (showStepButtons && !isAddon && isEmpty(values.displayStep)) {
            errors.displayStep = "- Please select an option";
        }

        return errors;
    };

interface QuestionFormModalProps {
    isOpen: boolean;
    modalType: QuestionnaireModalType;
    isAttendeeUnique?: boolean;
    onClose: () => void;
    field: FieldPayload;
    onSubmit: (field: FieldPayload) => void;
    isMembershipProduct?: boolean;
    experience?: Experience;
}

export const QuestionFormModal = ({
    isOpen,
    modalType,
    field,
    isAttendeeUnique,
    onClose,
    onSubmit,
    isMembershipProduct,
    experience,
}: QuestionFormModalProps) => {
    const titlePart = modalType === QuestionnaireModalType.PICKUP ? "Pickup Location" : "Question";
    let title = field?.id ? `Edit ${titlePart}` : `Add New ${titlePart}`;

    if (modalType === QuestionnaireModalType.ADDON) {
        title = field.label;
    }
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <Modal.Header>{title}</Modal.Header>

            <ModalContent
                modalType={modalType}
                field={field}
                isAttendeeUnique={isAttendeeUnique}
                isMembershipProduct={isMembershipProduct}
                experience={experience}
                onSubmit={onSubmit}
                onClose={onClose}
            />
        </Modal>
    );
};

const ModalContent = ({
    modalType,
    onSubmit,
    field,
    onClose,
    isAttendeeUnique,
    isMembershipProduct,
    experience,
}: Except<QuestionFormModalProps, "isOpen">) => {
    const { data: plugins } = useInstalledPlugins();
    const { viewConfigurations } = useQuestionnairePreference();
    const beforePaymentAllowed = isBeforePaymentType(modalType);
    const isAddon = modalType === QuestionnaireModalType.ADDON;
    const hasQuestionnairePlugin = plugins.some((plugin) => "questionnaire" in (plugin.abilities ?? {}));

    const showStepButtons =
        (hasX2CheckoutWithFullProbability() || beforePaymentAllowed) && hasQuestionnairePlugin && !isMembershipProduct;

    const initialValues = {
        ...field,
        organizerOnly: !isAttendeeUnique ? true : field.organizerOnly,
        displayStep:
            !isAttendeeUnique && field.displayStep === DisplayStepOption.WAIVER_SIGNING ? undefined : field.displayStep,
    };
    const hasWaiversEnabled = experience ? !!experience.waiverPreference?.remoteId : hasWaiverPlugin();

    return (
        <XolaFormik
            initialValues={initialValues}
            validate={validateForm(modalType, showStepButtons, field.displayStep)}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ handleSubmit, values }) => {
                return (
                    <Form>
                        <Modal.Body>
                            <QuestionFormFields modalType={modalType} />
                            {showStepButtons && !isAddon && <StepButtons experience={experience} />}
                            <QuestionFormSettings
                                isMembershipProduct={isMembershipProduct}
                                isAttendeeUnique={isAttendeeUnique}
                                hasQuestionnairePlugin={hasQuestionnairePlugin}
                                modalType={modalType}
                                hasWaiversEnabled={hasWaiversEnabled}
                            />
                            {!isMembershipProduct && <QuestionFormWaiverVisibility />}
                            {!isMembershipProduct && !isAddon && (
                                <QuestionFormRosterSettings viewConfigurations={viewConfigurations} field={field} />
                            )}

                            {values.type === FieldType.PICKUP && <QuestionPricingStructureSettings />}

                            {values.type === FieldType.PICKUP && <PickupLocationsList experience={experience} />}
                        </Modal.Body>
                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton
                                data-testid="questionnaire-form-modal-save-button"
                                type="button"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </Form>
                );
            }}
        </XolaFormik>
    );
};

const QuestionFormFields = ({ modalType }: { modalType: QuestionnaireModalType }) => {
    const { values } = useFormikContext<Field>();

    const showContentFields = ![
        QuestionnaireModalType.WEIGHT,
        QuestionnaireModalType.HEIGHT,
        QuestionnaireModalType.BIRTH_DATE,
        QuestionnaireModalType.ADDON,
    ].includes(modalType);

    const isConsentField = isMarketingConsentType(modalType);
    const isPredefined = PredefinedQuestions.some((info) => info.type === modalType);
    const isAddonField = modalType === QuestionnaireModalType.ADDON;

    return (
        <>
            {isAddonField ? (
                <AddonFormFields />
            ) : (
                <FormField label="Label" name="label">
                    <InputField disabled={isPredefined && !isConsentField} maxLength={100} />
                    <ContentLengthCounter value={values.label ?? ""} maxLength={100} />
                    <QuestionTypeField modalType={modalType} />
                </FormField>
            )}
            <ValidationRuleField modalType={modalType} />
            <UnitMeasurementField modalType={modalType} />
            {showContentFields && (
                <>
                    <FormField label="Description" name="description">
                        <InputField />
                    </FormField>
                    <FormField label="Link" name="url">
                        <InputField />
                    </FormField>
                </>
            )}
        </>
    );
};

const getAddonFieldType = (type: CatalogType) => {
    if (type === CatalogType.CHOICES) {
        return { label: "Dropdown Add-On" };
    }

    if (type === CatalogType.BOOLEAN) {
        return { label: "Checkbox Add-On" };
    }

    if (type === CatalogType.QUANTITY) {
        return { label: "Quantity Add-On" };
    }
};

const AddonFormFields = () => {
    const { values } = useFormikContext<Field>();

    if (!values.catalogItem) {
        return null;
    }

    const fieldType = getAddonFieldType(values.catalogItem?.type);

    return (
        <>
            <FormField label="Label" name="label" classNames={{ label: "text-gray" }}>
                <InputField disabled className="text-gray-darker" />
            </FormField>
            <FormGroup>
                <Label className="text-gray">Question Type</Label>
                <ComboBox isDisabled components={{ ValueContainer }} value={fieldType} />
            </FormGroup>
        </>
    );
};

// This component is used to customize the ValueContainer of the ComboBox for addon fields
// It adds a dropdown icon when the field type is "Dropdown Add-On"
// This is necessary because the ComboBox does not support custom components for the ValueContainer by default
// The icon is displayed only when the field type is "Dropdown Add-On"
// Type guard to check if value is an object with a label property
function hasLabel(obj: unknown): obj is { label: string } {
    return typeof obj === "object" && obj !== null && "label" in obj && typeof (obj as any).label === "string";
}

const ValueContainer = ({ children, ...props }: ValueContainerProps) => {
    // props contains methods and data passed by react-select, including:
    // - getValue(): () => [{ label: string, value: any }], other internal select methods/state
    const rawValue = props.getValue()?.[0];
    const isDropdownAddOn = hasLabel(rawValue) && rawValue.label === "Dropdown Add-On";

    return (
        components.ValueContainer && (
            <components.ValueContainer {...props}>
                <div className="flex items-center space-x-1 text-ellipsis">
                    {isDropdownAddOn && (
                        <div>
                            <DropdownIcon size="medium" />
                        </div>
                    )}
                    <NoTranslate as="div" className="grid items-center">
                        {children}
                    </NoTranslate>
                </div>
            </components.ValueContainer>
        )
    );
};

const QuestionFormWaiverVisibility = () => {
    const { values, setFieldValue, errors } = useFormikContext<Field>();

    if (values.displayStep !== DisplayStepOption.WAIVER_SIGNING) {
        return null;
    }

    const handleVisibilityChange = (value: boolean, targetVisibility: FieldVisibility) => {
        // If the visibility is set to ALL and the user toggles off one of the options, we need to change the visibility accordingly
        let newVisibility: FieldVisibility | undefined;

        if (values.visibility === FieldVisibility.ALL && !value) {
            if (targetVisibility === FieldVisibility.ADULTS) {
                newVisibility = FieldVisibility.MINORS;
            }
            if (targetVisibility === FieldVisibility.MINORS) {
                newVisibility = FieldVisibility.ADULTS;
            }
        }

        if (values.visibility === undefined && value) {
            newVisibility = targetVisibility;
        }

        // If the visibility is set to ADULTS or MINORS and the user toggles on the other option, we need to change the visibility to ALL
        if (values.visibility === FieldVisibility.ADULTS && targetVisibility === FieldVisibility.MINORS && value) {
            newVisibility = FieldVisibility.ALL;
        }

        if (values.visibility === FieldVisibility.MINORS && targetVisibility === FieldVisibility.ADULTS && value) {
            newVisibility = FieldVisibility.ALL;
        }

        setFieldValue("visibility", newVisibility);
    };

    return (
        <FormGroup className="flex flex-col space-y-2">
            <Label className={clsx("mb-0 flex-grow whitespace-nowrap", errors.visibility && "!text-danger")}>
                Collect for {errors.visibility}
            </Label>
            <Switch.Group>
                <Switch
                    isChecked={
                        values.visibility === FieldVisibility.ADULTS || values.visibility === FieldVisibility.ALL
                    }
                    onChange={(value: boolean) => handleVisibilityChange(value, FieldVisibility.ADULTS)}
                />
                <Switch.Label className="ml-3">Adults</Switch.Label>
            </Switch.Group>
            <Switch.Group>
                <Switch
                    isChecked={
                        values.visibility === FieldVisibility.MINORS || values.visibility === FieldVisibility.ALL
                    }
                    onChange={(value: boolean) => handleVisibilityChange(value, FieldVisibility.MINORS)}
                />
                <Switch.Label className="ml-3">Minors</Switch.Label>
            </Switch.Group>
        </FormGroup>
    );
};

const addonModalTypes = new Set([QuestionnaireModalType.ADDON, QuestionnaireModalType.PICKUP]);

export const QuestionFormRosterSettings = ({
    viewConfigurations,
    field,
}: {
    viewConfigurations?: RosterViewConfiguration[];
    field?: FieldPayload;
}) => {
    const { values, setFieldValue } = useFormikContext<Field>();

    return (
        <FormGroup className="flex flex-col space-y-1">
            <Label className="mb-0 flex-grow">Roster Settings</Label>
            <div data-testid="questionnaire-form-modal-roster-export-toggle">
                <Switch.Group>
                    <Switch isChecked={values.roster} onChange={(value: boolean) => setFieldValue("roster", value)} />
                    <Switch.Label className="ml-3">Include in roster export</Switch.Label>
                </Switch.Group>
            </div>
            {viewConfigurations && viewConfigurations.length > 1 && !field?.id && (
                <Switch.Group>
                    <Switch
                        isChecked={values.addToSavedViews}
                        onChange={(value: boolean) => setFieldValue("addToSavedViews", value)}
                    />
                    <Switch.Label className="ml-3">Include in all existing saved roster views</Switch.Label>
                </Switch.Group>
            )}
        </FormGroup>
    );
};

export const QuestionPricingStructureSettings = () => {
    const { values, setFieldValue } = useFormikContext<FieldWithPickupOptions>();

    return (
        <FormGroup className="flex flex-col space-y-1">
            <Label className="mb-0 flex-grow">Pricing Structure</Label>
            <div className="flex flex-col gap-1">
                <div data-testid="questionnaire-pricing-per-person-radio">
                    <RadioInput
                        checked={values.priceType === QuestionnairePricingStructure.PER_PERSON}
                        onChange={() => setFieldValue("priceType", QuestionnairePricingStructure.PER_PERSON)}
                    >
                        Per guest
                    </RadioInput>
                </div>
                <div data-testid="questionnaire-pricing-per-group-radio">
                    <RadioInput
                        checked={values.priceType === QuestionnairePricingStructure.PER_GROUP}
                        onChange={() => setFieldValue("priceType", QuestionnairePricingStructure.PER_GROUP)}
                    >
                        Per group
                    </RadioInput>
                </div>
            </div>
        </FormGroup>
    );
};

export const BeforePaymentField = ({
    modalType,
    isMembershipProduct,
}: {
    modalType: QuestionnaireModalType;
    isMembershipProduct?: boolean;
}) => {
    const { values, setFieldValue } = useFormikContext<Field>();

    const isAddonField = addonModalTypes.has(modalType);
    const isDisabled = isAddonField || isMembershipProduct;

    return (
        <Switch.Group>
            <Tooltip content="For Memberships, all questions are asked before payment." disabled={!isMembershipProduct}>
                <Switch
                    data-testid="questionnaire-form-modal-ask-before-payment-toggle"
                    disabled={isDisabled}
                    isChecked={values.displayStep === DisplayStepOption.BEFORE_PAYMENT || isAddonField}
                    onChange={(value: boolean) =>
                        setFieldValue(
                            "displayStep",
                            value ? DisplayStepOption.BEFORE_PAYMENT : DisplayStepOption.AFTER_PAYMENT,
                        )
                    }
                />
            </Tooltip>
            <Switch.Label className="ml-3">Ask before payment</Switch.Label>
        </Switch.Group>
    );
};

const steps: { label: string; value: DisplayStepOption }[] = [
    { label: "Before Payment", value: DisplayStepOption.BEFORE_PAYMENT },
    { label: "After Payment", value: DisplayStepOption.AFTER_PAYMENT },
    { label: "Waiver Signing", value: DisplayStepOption.WAIVER_SIGNING },
];

export const StepButtons = ({ experience }: { experience?: Experience }) => {
    const { values, errors, setFieldValue } = useFormikContext<Field>();

    const activeIndex = steps.findIndex((step) => step.value === values.displayStep);
    const hasWaiversEnabled = experience ? !!experience.waiverPreference?.remoteId : hasWaiverPlugin();
    const isPickupType = values.type === FieldType.PICKUP;

    const handleChange = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
        const step = steps[index];
        setFieldValue("displayStep", step.value);
        if (step.value === DisplayStepOption.WAIVER_SIGNING) {
            setFieldValue("organizerOnly", false);
        }
        e.preventDefault();
        e.stopPropagation();
    };

    return (
        <>
            <Label className={clsx("mb-0 flex-grow", errors.displayStep && "!text-danger")}>
                When to Ask {errors.displayStep}
            </Label>
            <div>
                <ButtonGroup
                    isCollapsed
                    className={clsx(
                        "flex-level mb-4 mt-1 flex items-center justify-between",
                        errors.displayStep && "rounded border border-danger",
                    )}
                    size="small"
                    value={activeIndex}
                >
                    {steps.map((step) => {
                        return (
                            <ButtonGroup.Button
                                key={step}
                                disabled={
                                    (step.value === DisplayStepOption.WAIVER_SIGNING && !hasWaiversEnabled) ||
                                    (isPickupType && step.value !== DisplayStepOption.BEFORE_PAYMENT)
                                }
                                className="h-7.5 px-3 text-base"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                                    handleChange(e, steps.indexOf(step))
                                }
                            >
                                <div className="w-full space-x-1">{step.label}</div>
                            </ButtonGroup.Button>
                        );
                    })}
                </ButtonGroup>
            </div>
        </>
    );
};
